import { ReactNode, useCallback, useReducer } from 'react';

import { SpinnerContext } from '@src/contexts/spinner-context';

enum actions {
  SHOW_SPINNER = 'SHOW',
  HIDE_SPINNER = 'HIDE',
}

/**
 * The spinner context provider uses a semaphore to determine if the spinner
 * should be displayed. This means that if you show the spinner multiple times,
 * it needs to be dismissed the same amount of times. Since this is intended to
 * be used application wide, if you have multiple APIs that are loading, the
 * spinner doesn't go away until they are all finished and they can
 * independently control their own spinner behavior.
 */
const reducer = (state: number, action: { type: actions }): number => {
  switch (action.type) {
    case actions.SHOW_SPINNER: {
      return state + 1;
    }
    case actions.HIDE_SPINNER: {
      return state < 1 ? 0 : state - 1;
    }
    default: {
      throw Error(
        'Unknown action in spinner reducer: ' + (action.type as string),
      );
    }
  }
};

export const SpinnerContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, 0);

  const value = {
    isSpinnerVisible: state > 0,
    showSpinner: useCallback(
      () => dispatch({ type: actions.SHOW_SPINNER }),
      [],
    ),
    hideSpinner: useCallback(
      () => dispatch({ type: actions.HIDE_SPINNER }),
      [],
    ),
  };

  return (
    <SpinnerContext.Provider value={value}>{children}</SpinnerContext.Provider>
  );
};
